import { useMemo } from 'react';

import { useBehaviorController_getOverview } from '@/services/hooks';
import type { OverviewResponseDto } from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import { mapOverviewResponseToDomain } from './mappers';
import { toBehaviorScopeQuery } from './queryParams';
import type { BehaviorScopeParams, UseGetBehaviorOverviewResult } from './types';

const EMPTY_OVERVIEW_DATA: OverviewResponseDto = {
    averages: {
        swimSpeed: undefined,
        activeRespiration: undefined,
        tilt: undefined,
        numberOfSwimSpeedMeasurements: undefined,
        numberOfRespirationMeasurements: undefined,
        numberOfTiltMeasurements: undefined,
    },
    environmentalFactors: {
        oxygenConcentration: undefined,
        oxygenSaturation: undefined,
        temperature: undefined,
        depth: undefined,
        salinity: undefined,
        lastMeasurement: '',
    },
    respirationPercentages: { passive: undefined, active: undefined, lastMeasurement: '' },
    isBioscopeRecentlyDeployed: false,
};

/**
 * The behavior overview for the cage, or for the fish group named by `fishGroupId`. For a
 * group the averages and respiration are its last values in its last stay; the environmental
 * factors stay the pen's.
 */
export const useGetBehaviorOverview = (
    cageId: number,
    params: BehaviorScopeParams = {},
    options?: DomainQueryOptions<OverviewResponseDto>
): UseGetBehaviorOverviewResult => {
    const overviewQuery = useBehaviorController_getOverview(
        cageId,
        toBehaviorScopeQuery(params),
        options
    );

    const overviewData = useMemo(
        () => mapOverviewResponseToDomain(overviewQuery.data?.data ?? EMPTY_OVERVIEW_DATA),
        [overviewQuery.data?.data]
    );

    return {
        ...overviewQuery,
        isLoading: overviewQuery.isPending,
        data: overviewData,
    };
};
